/* eslint-disable no-undef */
import {
  Button,
  Dialog,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CheckRounded, Close, GetApp } from '@material-ui/icons';
import MaterialPhoneInput from '@orientaction/components/MaterialPhoneInput';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { FC, useCallback, useRef, useState } from 'react';
import { apiServiceNative } from '../../../utils/fetchNative';

interface IProps {
  open: boolean;
  onClose: () => void;
  defaultEmail?: string;
  defaultName?: string;
  defaultFirstName?: string;
  defaultPhone?: string;
}

const SUBJECTS = ['Signaler un bug', 'Problème de connexion', 'Question sur un test', 'Autre'];

const ACCEPTED_FORMATS = '.pdf,.doc,.docx,.jpg,.jpeg,.png';
const MAX_FILE_SIZE_MB = 100;

const HelpModal: FC<IProps> = ({
  open,
  onClose,
  defaultEmail = '',
  defaultName = '',
  defaultFirstName = '',
  defaultPhone = '',
}) => {
  const cls = useStyles();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    email: defaultEmail,
    nom: defaultName,
    prenom: defaultFirstName,
    phone: defaultPhone,
    sujet: '',
    commentaire: '',
  });
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (field: string) => (e: any) => {
    setForm({ ...form, [field]: e.target.value });
    setSubmitError('');
  };

  const handlePhoneChange = (value?: string) => {
    setForm({ ...form, phone: value || '' });
    setSubmitError('');
  };

  const validateFile = useCallback((f: File): boolean => {
    setFileError('');
    const ext = f.name.split('.').pop()?.toLowerCase() || '';
    const validExts = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'];
    if (!validExts.includes(ext)) {
      setFileError('Format non accepté. Utilisez PDF, DOC, DOCX, JPG ou PNG.');
      return false;
    }
    if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setFileError('Le fichier dépasse la taille maximale de 100 Mo.');
      return false;
    }
    return true;
  }, []);

  const handleFileDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile && validateFile(droppedFile)) {
        setFile(droppedFile);
      }
    },
    [validateFile]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files?.[0];
      if (selected && validateFile(selected)) {
        setFile(selected);
      }
    },
    [validateFile]
  );

  const isPhoneValid =
    !!form.phone && form.phone.length > 2 && isValidPhoneNumber(form.phone);

  const handleSubmit = async () => {
    if (!form.email || !form.nom || !form.prenom || !isPhoneValid || !form.sujet || !form.commentaire) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const formData = new FormData();
      formData.append('email', form.email);
      formData.append('nom', form.nom);
      formData.append('prenom', form.prenom);
      formData.append('phone', form.phone);
      formData.append('sujet', form.sujet);
      formData.append('commentaire', form.commentaire);
      if (file) {
        formData.append('screenshot', file);
      }

      await apiServiceNative.post('/api/users-permissions/user/help-request', formData, true);

      setSubmitted(true);
    } catch {
      setSubmitError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid =
    form.email && form.nom && form.prenom && isPhoneValid && form.sujet && form.commentaire;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth={true}
      PaperProps={{ className: cls.paper }}>
      {submitted ? (
        <div className={cls.confirmationContainer}>
          <IconButton className={cls.confirmCloseButton} onClick={onClose}>
            <Close />
          </IconButton>
          <div className={cls.confirmIconWrapper}>
            <CheckRounded className={cls.confirmIcon} />
          </div>
          <h2 className={cls.confirmTitle}>Confirmation</h2>
          <p className={cls.confirmDescription}>
            Votre demande a bien été envoyée. Notre équipe est à votre écoute et reviendra vers vous
            dans les meilleurs délais.
          </p>
          <Button className={cls.confirmButton} fullWidth={true} onClick={onClose}>
            Fermer
          </Button>
        </div>
      ) : (
        <>
          <div className={cls.header}>
            <h2 className={cls.title}>Besoin d&apos;aide</h2>
            <IconButton className={cls.closeButton} onClick={onClose}>
              <Close />
            </IconButton>
          </div>

          <p className={cls.description}>
            Notre équipe est à votre écoute. Merci de nous préciser votre demande via le formulaire
            ci-dessous afin que nous puissions vous accompagner au mieux.
          </p>

          <div className={cls.form}>
            <div className={cls.field}>
              <InputLabel className={cls.label}>Email</InputLabel>
              <TextField
                variant="outlined"
                fullWidth={true}
                size="small"
                type="email"
                value={form.email}
                onChange={handleChange('email')}
                placeholder="Votre adresse email"
                InputProps={{ className: cls.input }}
              />
            </div>

            <div className={cls.field}>
              <InputLabel className={cls.label}>Nom</InputLabel>
              <TextField
                variant="outlined"
                fullWidth={true}
                size="small"
                value={form.nom}
                onChange={handleChange('nom')}
                placeholder="Votre nom"
                InputProps={{ className: cls.input }}
              />
            </div>

            <div className={cls.field}>
              <InputLabel className={cls.label}>Prénom</InputLabel>
              <TextField
                variant="outlined"
                fullWidth={true}
                size="small"
                value={form.prenom}
                onChange={handleChange('prenom')}
                placeholder="Votre prénom"
                InputProps={{ className: cls.input }}
              />
            </div>

            <div className={cls.field}>
              <InputLabel className={cls.label}>Téléphone</InputLabel>
              <MaterialPhoneInput
                value={form.phone as any}
                onChange={handlePhoneChange}
                error={!!form.phone && !isPhoneValid}
                style={{ border: '1px solid #CDCEDA', borderRadius: 8, height: 40 }}
              />
            </div>

            <div className={cls.field}>
              <InputLabel className={cls.label}>Sujet</InputLabel>
              <FormControl variant="outlined" fullWidth={true} size="small">
                <Select
                  value={form.sujet}
                  onChange={handleChange('sujet')}
                  displayEmpty={true}
                  className={cls.input}
                  renderValue={(selected: any) => {
                    if (!selected) {
                      return <span className={cls.placeholder}>Sélectionnez un sujet</span>;
                    }
                    return selected;
                  }}>
                  {SUBJECTS.map(subject => (
                    <MenuItem key={subject} value={subject}>
                      {subject}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className={cls.field}>
              <InputLabel className={cls.label}>Commentaire</InputLabel>
              <TextField
                variant="outlined"
                fullWidth={true}
                multiline={true}
                minRows={4}
                maxRows={6}
                value={form.commentaire}
                onChange={handleChange('commentaire')}
                placeholder="Décrivez votre problème..."
                InputProps={{ className: cls.input }}
              />
            </div>

            <div className={cls.field}>
              <div className={cls.labelRow}>
                <InputLabel className={cls.label}>Capture d&apos;écran</InputLabel>
                <span className={cls.optional}>Facultatif</span>
              </div>
              <div
                className={`${cls.dropZone} ${isDragging ? cls.dropZoneActive : ''} ${
                  fileError ? cls.dropZoneError : ''
                }`}
                onDragOver={e => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleFileDrop}
                onClick={() => fileInputRef.current?.click()}
                role="button"
                tabIndex={0}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_FORMATS}
                  onChange={handleFileSelect}
                  hidden={true}
                />
                {file ? (
                  <div className={cls.fileInfo}>
                    <span className={cls.fileName}>{file.name}</span>
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.stopPropagation();
                        setFile(null);
                        setFileError('');
                      }}>
                      <Close fontSize="small" />
                    </IconButton>
                  </div>
                ) : (
                  <>
                    <GetApp className={cls.uploadIcon} />
                    <span className={cls.uploadText}>
                      Cliquez ou faites glisser ici pour importer
                    </span>
                    <span className={cls.uploadFormats}>
                      Formats acceptés : PDF, DOC, DOCX, JPG, PNG (max. 100Mo)
                    </span>
                  </>
                )}
              </div>
              {fileError && <p className={cls.errorText}>{fileError}</p>}
            </div>

            {submitError && <p className={cls.errorText}>{submitError}</p>}

            <Button
              className={cls.submitButton}
              onClick={handleSubmit}
              disabled={!isFormValid || isSubmitting}
              fullWidth={true}>
              {isSubmitting ? 'Envoi en cours...' : 'Envoyer'}
            </Button>
          </div>
        </>
      )}
    </Dialog>
  );
};

const useStyles = makeStyles(() => ({
  paper: {
    borderRadius: 16,
    padding: '28px 32px 32px',
    maxHeight: '90vh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  title: {
    fontFamily: 'Poppins',
    fontWeight: 700,
    fontSize: 24,
    color: '#000A8C',
    margin: 0,
  },
  closeButton: {
    padding: 4,
    color: '#0E1247',
    marginTop: -4,
    marginRight: -4,
  },
  description: {
    fontFamily: 'Poppins',
    fontSize: 14,
    lineHeight: 1.6,
    color: '#0E1247',
    margin: '0 0 24px 0',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: 13,
    color: '#0E1247',
    marginBottom: 6,
    transform: 'none',
    position: 'relative',
  },
  labelRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  optional: {
    fontFamily: 'Poppins',
    fontSize: 12,
    color: '#9CA3AF',
  },
  input: {
    fontFamily: 'Poppins',
    fontSize: 14,
    borderRadius: 8,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#CDCEDA',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#000A8C',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#000A8C',
      borderWidth: 1.5,
    },
    '& input': {
      fontFamily: 'Poppins !important',
    },
    '& .MuiSelect-iconOutlined': {
      color: '#000A8C !important',
    },
  },
  placeholder: {
    color: '#9CA3AF',
  },
  dropZone: {
    border: '1.5px dashed #CDCEDA',
    borderRadius: 12,
    padding: '24px 16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.2s ease, background-color 0.2s ease',
    backgroundColor: '#FAFAFA',
    '&:hover': {
      borderColor: '#000A8C',
      backgroundColor: '#F7F7FB',
    },
  },
  dropZoneActive: {
    borderColor: '#000A8C',
    backgroundColor: '#EEF0FF',
  },
  uploadIcon: {
    fontSize: 28,
    color: '#0E1247',
    marginBottom: 8,
    transform: 'rotate(180deg)',
  },
  uploadText: {
    fontFamily: 'Poppins',
    fontWeight: 500,
    fontSize: 13,
    color: '#0E1247',
    marginBottom: 4,
  },
  uploadFormats: {
    fontFamily: 'Poppins',
    fontSize: 11,
    color: '#9CA3AF',
  },
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  fileName: {
    fontFamily: 'Poppins',
    fontSize: 13,
    fontWeight: 500,
    color: '#000A8C',
  },
  dropZoneError: {
    borderColor: '#E63C3C',
  },
  errorText: {
    fontFamily: 'Poppins',
    fontSize: 12,
    color: '#E63C3C',
    margin: '6px 0 0 0',
  },
  confirmationContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '16px 0 8px',
    position: 'relative',
  },
  confirmCloseButton: {
    position: 'absolute',
    top: 0,
    right: -16,
    padding: 4,
    color: '#0E1247',
  },
  confirmIconWrapper: {
    width: 64,
    height: 64,
    borderRadius: '20%',
    backgroundColor: '#EEEDF7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  confirmIcon: {
    fontSize: 32,
    color: '#000A8C',
  },
  confirmTitle: {
    fontFamily: 'Poppins',
    fontWeight: 700,
    fontSize: 24,
    color: '#000A8D',
    margin: '0 0 12px 0',
  },
  confirmDescription: {
    fontFamily: 'Poppins',
    fontSize: 14,
    lineHeight: 1.7,
    color: '#6B7280',
    margin: '0 0 28px 0',
    maxWidth: 340,
  },
  confirmButton: {
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: 15,
    textTransform: 'none' as const,
    color: '#FFFFFF',
    backgroundColor: '#000A8C',
    borderRadius: 8,
    padding: '12px 24px',
    '&:hover': {
      backgroundColor: '#0E1247',
    },
  },
  submitButton: {
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: 15,
    textTransform: 'none',
    color: '#FFFFFF',
    backgroundColor: '#000A8C',
    borderRadius: 8,
    padding: '12px 24px',
    marginTop: 8,
    '&:hover': {
      backgroundColor: '#0E1247',
    },
    '&:disabled': {
      backgroundColor: '#CDCEDA',
      color: '#FFFFFF',
    },
  },
}));

export default HelpModal;
